import { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Alert, Button, Space, Switch, Tooltip, Typography } from "antd";
import { loadStripe } from "@stripe/stripe-js";
import { IslandLayout } from "../../../layouts/island-layout/IslandLayout.jsx";
import { SubscriptionPlanCard } from "./SubscriptionPlanCard.jsx";
import { UnstractGrowthPlan, UnstractStarterPlan } from "../../assets/index.js";
import "./UnstractSubscription.css";
import { UNSTRACT_SUBSCRIPTION_PLANS } from "../helper/constants.js";
import { useUnstractSubscriptionPlanStore } from "../../store/unstract-subscription-plan-store.js";
import { useNavigate } from "react-router-dom";
import { useSessionStore } from "../../../store/session-store.js";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate.js";
import {
  formatNumberWithCommas,
  getReadableDateAndTime,
  TRIAL_PLAN,
} from "../../../helpers/GetStaticData.js";
import { useAlertStore } from "../../../store/alert-store.js";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler.jsx";
import { SpinnerLoader } from "../../../components/widgets/spinner-loader/SpinnerLoader.jsx";
import useRequestUrl from "../../../hooks/useRequestUrl.js";

const initialPlans = [
  {
    id: "unstract-cloud-starter",
    image: <UnstractStarterPlan />,
    planName: UNSTRACT_SUBSCRIPTION_PLANS.STARTER,
  },
  {
    id: "unstract-cloud-growth",
    image: <UnstractGrowthPlan />,
    planName: UNSTRACT_SUBSCRIPTION_PLANS.GROWTH,
  },
];

function UnstractSubscription() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [stripePromise, setStripePromise] = useState(null);
  const [isSubscriptionPlanLoading, setIsSubscriptionPlanLoading] =
    useState(true);

  const navigate = useNavigate();
  const { sessionDetails } = useSessionStore();
  const { unstractSubscriptionPlan, updateSubscriptionPlan } =
    useUnstractSubscriptionPlanStore();
  const axiosPrivate = useAxiosPrivate();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const { getUrl } = useRequestUrl();

  const DASHBOARD_PATH = `/${sessionDetails?.orgName}/dashboard`;

  // Determine if user already has a paid subscription
  const existingPlanMsg = useMemo(() => {
    const { subscriptionId, planType, product, isCancelled, nextBillingDate } =
      unstractSubscriptionPlan || {};

    if (!subscriptionId || planType === TRIAL_PLAN) {
      return "";
    }

    const planName = product?.name
      ? product.name.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
      : "";

    // If subscription has been cancelled, show a valid-until message
    if (isCancelled) {
      return `Your subscription to ${planName} has been cancelled. You still have access until ${getReadableDateAndTime(
        nextBillingDate,
        false
      )}.`;
    }

    // Otherwise, show the upcoming billing date
    return `You’re currently subscribed to ${planName}. Your next billing date is on ${getReadableDateAndTime(
      nextBillingDate,
      false
    )}.`;
  }, [unstractSubscriptionPlan]);

  const handleToggle = useCallback(() => {
    setIsAnnual((prev) => !prev);
  }, []);

  const fetchPlanData = useCallback(async () => {
    if (!sessionDetails?.orgId) return;

    try {
      setIsSubscriptionPlanLoading(true);
      const requestOptions = {
        method: "GET",
        url: getUrl("/pricing/"),
      };
      const res = await axiosPrivate(requestOptions);
      const data = res?.data || [];

      const newPlans = initialPlans.map((plan) => {
        const monthlyPlan = data.find((item) => item?.id === plan?.id);
        const annualPlan = data.find(
          (item) => item?.id === `${plan?.id}-annual`
        );

        return {
          ...plan,
          numberOfPagesPerMonth: formatNumberWithCommas(
            monthlyPlan?.metadata?.pages_processed_limit
          ),
          numberOfPagesPerYear: formatNumberWithCommas(
            annualPlan?.metadata?.pages_processed_limit
          ),
          overagePerMonth: formatNumberWithCommas(
            monthlyPlan?.metadata?.overage
          ),
          overagePerYear: formatNumberWithCommas(annualPlan?.metadata?.overage),
          pricePerMonth: formatNumberWithCommas(monthlyPlan?.metadata?.price),
          pricePerYear: formatNumberWithCommas(
            Math.round((annualPlan?.metadata?.price || 0) / 12)
          ),
        };
      });

      setSubscriptionPlans(newPlans);
    } catch (error) {
      setAlertDetails(handleException(error));
    } finally {
      setIsSubscriptionPlanLoading(false);
    }
  }, []);

  const loadStripeEnv = useCallback(async () => {
    if (!sessionDetails?.orgId) return;

    const requestOptions = {
      method: "GET",
      url: getUrl("/subscription/env/"),
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };

    try {
      const res = await axiosPrivate(requestOptions);
      const stripePublicKey = res?.data?.stripe_public_key;
      if (stripePublicKey) {
        setStripePromise(loadStripe(stripePublicKey));
      }
    } catch (err) {
      setAlertDetails(handleException(err));
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    if (unstractSubscriptionPlan?.isCancelled) {
      setAlertDetails({
        type: "error",
        content: "Subscription plan is already cancelled.",
      });
      return;
    }

    const requestOptions = {
      method: "POST",
      url: getUrl("/subscription/cancel/"),
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
      data: {
        product_id: unstractSubscriptionPlan?.subscriptionId,
      },
    };

    try {
      await axiosPrivate(requestOptions);
      updateSubscriptionPlan({
        isCancelled: true,
      });
      setAlertDetails({
        type: "success",
        content: "Cancelled successfully",
      });
    } catch (err) {
      setAlertDetails(handleException(err));
    }
  }, []);

  // Fetch plan data & load stripe env on mount
  useEffect(() => {
    fetchPlanData();
    if (
      unstractSubscriptionPlan?.planType !== "PAID" ||
      unstractSubscriptionPlan?.isCancelled
    ) {
      loadStripeEnv();
    }
  }, []);

  if (isSubscriptionPlanLoading) {
    return <SpinnerLoader />;
  }

  return (
    <div className="page-bg-2 flex-dir-col">
      <div className="subscription-header">
        <Space>
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(DASHBOARD_PATH)}
            disabled={unstractSubscriptionPlan?.remainingDays <= 0}
          />
          <Typography.Text className="subscription-font-size" strong>
            Pricing
          </Typography.Text>
        </Space>
      </div>

      <div className="flex-1 overflow-hidden">
        <IslandLayout>
          <div className="display-flex-center height-100 overflow-y-auto">
            <Space className="pad-top-20" direction="vertical" size={40}>
              {existingPlanMsg && (
                <div className="display-flex-center">
                  <Alert
                    className="subscription-alert-box"
                    message={
                      <div className="text-align-center">
                        <strong>{existingPlanMsg}</strong>
                      </div>
                    }
                    description={
                      <div className="link-container">
                        <Button
                          type="link"
                          onClick={() => navigate(DASHBOARD_PATH)}
                        >
                          See usage details
                        </Button>
                        <Tooltip title="It will take you to the Stripe dashboard, where you can view your past invoices.">
                          <Button
                            type="link"
                            href="https://billing.stripe.com/p/login/cN2bKwcBz9jrcDe4gg"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Past Invoices
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={
                            unstractSubscriptionPlan?.isCancelled &&
                            "Subscription plan is already cancelled."
                          }
                        >
                          <Button
                            type="link"
                            onClick={cancelSubscription}
                            disabled={unstractSubscriptionPlan?.isCancelled}
                          >
                            Unsubscribe
                          </Button>
                        </Tooltip>
                      </div>
                    }
                  />
                </div>
              )}
              {subscriptionPlans?.length > 0 ? (
                <>
                  <div className="display-flex-center">
                    <Space size={20}>
                      <Typography.Text
                        className="subscription-plan-text"
                        strong
                      >
                        Monthly
                      </Typography.Text>
                      <Switch
                        defaultChecked={isAnnual}
                        onChange={handleToggle}
                      />
                      <Typography.Text
                        className="subscription-plan-text"
                        strong
                      >
                        Annual
                      </Typography.Text>
                    </Space>
                  </div>
                  <div>
                    {subscriptionPlans.map((plan) => (
                      <Space key={plan.id}>
                        <div className="subscription-plan-card-wrapper">
                          <SubscriptionPlanCard
                            plan={plan}
                            isAnnual={isAnnual}
                            stripePromise={stripePromise}
                            isAlreadySubscribed={!!existingPlanMsg}
                            isSubscriptionCancelled={
                              unstractSubscriptionPlan?.isCancelled
                            }
                          />
                        </div>
                      </Space>
                    ))}
                  </div>
                </>
              ) : (
                <Typography.Text>
                  Failed to load the pricing details
                </Typography.Text>
              )}
              <div className="center pad-bottom-20">
                <Alert
                  showIcon
                  message={
                    "Pricing is inclusive of LLMWhisperer for the same number of pages."
                  }
                />
              </div>
            </Space>
          </div>
        </IslandLayout>
      </div>
    </div>
  );
}

export { UnstractSubscription };
